import type {
  ElementState,
  VisibleState,
} from '@wix/ambassador-devcenter-v1-component-type-data/types';
import {
  InlineElement,
  type EditorElement,
} from '@wix/component-protocol/schema';
import type { CompRef } from '@wix/document-services-types';
import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';
import { HierarchyPlatformService } from './HierarchyPlatformService';
import { InnerElementPathPlatformService } from './InnerElementPathPlatformService';
import type { EditorPointer } from '@wix/editor-document-logic-api';

interface ExtendedElementState extends ElementState {
  target?: {
    elementPath?: string;
  };
}

export interface ResetStatesOptions {
  /**
   * When true, also resets the visibility state on the owner pointer resolved
   * from the component's `visibleState` (the same pointer `activateVisibilityState`
   * sets). Lets callers that hold the activated pointer close a visibility state
   * (e.g. a hamburger menu) without reaching for `resetAllStates`.
   */
  includeVisibilityState?: boolean;
}

export class StatesPlatformService extends AbstractComponentsPlatformService {
  readonly hierarchyService: HierarchyPlatformService;
  readonly innerElementPathService: InnerElementPathPlatformService;

  constructor(
    ...args: ConstructorParameters<typeof AbstractComponentsPlatformService>
  ) {
    super(...args);
    this.hierarchyService = new HierarchyPlatformService(...args);
    this.innerElementPathService = new InnerElementPathPlatformService(...args);
  }

  getStateDefinitions(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ): NonNullable<EditorElement['states']> {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .resolver.getStateDefinitions();
  }

  getDesignStateDefinitions(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ): NonNullable<EditorElement['states']> {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .resolver.getDesignStateDefinitions();
  }

  getState(editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>) {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .query.getState();
  }

  setState(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
    state: string,
  ) {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .query.setState(state);
  }

  resetStates(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
    options?: ResetStatesOptions,
  ) {
    if (options?.includeVisibilityState) {
      const pointers = Array.isArray(editorPointers)
        ? editorPointers
        : [editorPointers];

      pointers.forEach((editorPointer) =>
        this.#resetVisibilityState(editorPointer),
      );
    }

    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .query.resetStates();
  }

  getActiveStates() {
    const statesHandler =
      this.virtualElementProvider.getManifestQueryAPI().state;

    return statesHandler
      .getGlobalStates()
      .map(({ editorPointer, state }) => ({ editorPointer, state }));
  }

  resetAllStates() {
    const { state: statesHandler, props: propsHandler } =
      this.virtualElementProvider.getManifestQueryAPI();

    propsHandler.removeAllPropsOverrides();

    statesHandler.getGlobalStates().forEach(({ editorPointer, type }) => {
      statesHandler.resetCurrentStates(editorPointer, type);
    });
  }

  getStateFocusedElement(
    editorPointer: EditorPointer<CompRef>,
    stateKey?: string,
  ): EditorPointer<CompRef> | null {
    const ownerPointer = this.#resolveStateOwnerPointer(editorPointer);
    const stateDefinitions = this.getStateDefinitions([ownerPointer]);
    const state = stateDefinitions[
      stateKey ?? this.getState([ownerPointer]) ?? ''
    ] as ExtendedElementState;

    const elementPath = state?.target?.elementPath;

    if (!elementPath) {
      return null;
    }

    const focusedPointer = this.innerElementPathService.getPointer(
      ownerPointer,
      elementPath,
    );

    if (!focusedPointer.innerElementPath?.length) {
      return null;
    }

    return focusedPointer;
  }

  reconcileStatesOnComponentSelection(selectedComponentRefs: CompRef[]) {
    const stateHandler =
      this.virtualElementProvider.getManifestQueryAPI().state;

    const selectedPointerKeys = new Set(
      selectedComponentRefs.map((compRef) =>
        this.editorPointerAPI.getEditorPointerUniqueId(compRef, [], {
          ignoreVariants: true,
        }),
      ),
    );

    const states = stateHandler.getGlobalStates();

    states.forEach((stateEntry) => {
      const { editorPointer } = stateEntry;
      const editorPointerKey = this.editorPointerAPI.getEditorPointerUniqueId(
        editorPointer.documentPointer,
        editorPointer.innerElementPath,
        { ignoreVariants: true },
      );

      if (selectedPointerKeys.has(editorPointerKey)) {
        return;
      }

      const componentBuilderHierarchy = this.hierarchyService.getChildren(
        editorPointer,
        { isRecursive: true },
      );
      const componentDmHierarchy = this.dm.components
        .getChildren(editorPointer.documentPointer, true)
        .map((compRef) => this.editorPointerAPI.buildEditorPointer(compRef));

      const componentHierarchy = [
        editorPointer,
        ...componentBuilderHierarchy,
        ...componentDmHierarchy,
      ];

      const isWithinHierarchy = componentHierarchy.some((hierarchyPointer) => {
        const hierarchyPointerKey =
          this.editorPointerAPI.getEditorPointerUniqueId(
            hierarchyPointer.documentPointer,
            hierarchyPointer.innerElementPath,
            { ignoreVariants: true },
          );
        return selectedPointerKeys.has(hierarchyPointerKey);
      });

      if (!isWithinHierarchy) {
        this.resetStates([editorPointer]);
      }
    });
  }

  #getInlineManifest(
    editorPointer: EditorPointer<CompRef>,
  ): InlineElement | undefined {
    return this.virtualElementProvider
      .buildVirtualElement({ editorPointers: [editorPointer] })
      .getManifest()?.editorElement as InlineElement | undefined;
  }

  #resolveStateOwnerPointer(
    editorPointer: EditorPointer<CompRef>,
  ): EditorPointer<CompRef> {
    const elementPath =
      this.#getInlineManifest(editorPointer)?.visibleState?.elementPath;

    if (elementPath) {
      return this.innerElementPathService.getPointer(
        editorPointer,
        elementPath,
      );
    }

    const ancestors = [
      ...this.hierarchyService.getAncestors(editorPointer),
    ].reverse();

    for (const ancestorPointer of ancestors) {
      const ancestorElementPath =
        this.#getInlineManifest(ancestorPointer)?.visibleState?.elementPath;

      if (ancestorElementPath) {
        return this.innerElementPathService.getPointer(
          ancestorPointer,
          ancestorElementPath,
        );
      }
    }

    return editorPointer;
  }

  #resolveVisibleState(editorPointer: EditorPointer<CompRef>): {
    visibleState: VisibleState;
    relativePointer: EditorPointer<CompRef>;
  } {
    const manifest = this.#getInlineManifest(editorPointer);

    if (manifest?.visibleState) {
      return {
        visibleState: manifest.visibleState,
        relativePointer: editorPointer,
      };
    }

    const ancestors = [
      ...this.hierarchyService.getAncestors(editorPointer),
    ].reverse();

    for (const ancestorPointer of ancestors) {
      const ancestorManifest = this.#getInlineManifest(ancestorPointer);

      const visibleState = ancestorManifest?.visibleState;

      if (visibleState) {
        return { visibleState, relativePointer: ancestorPointer };
      }
    }

    return {
      visibleState: {
        stateKey: 'regular',
        elementPath: '.root',
      },
      relativePointer: editorPointer,
    };
  }

  getDesignStates(editorPointers: EditorPointer<CompRef>[]): string[] {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .query.getDesignState();
  }

  setDesignStates(
    editorPointers: EditorPointer<CompRef>[],
    states: string[],
  ): void {
    this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .query.setDesignState(states);
    this.manifestChangeDetection.notifyGlobalStatePatch();
  }

  resetDesignStates(editorPointers: EditorPointer<CompRef>[]): void {
    this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .query.resetDesignState();
    this.manifestChangeDetection.notifyGlobalStatePatch();
  }

  #resolveVisibilityStateOwner(editorPointer: EditorPointer<CompRef>): {
    ownerPointer: EditorPointer<CompRef>;
    stateKey: string;
  } | null {
    const { visibleState, relativePointer } =
      this.#resolveVisibleState(editorPointer);

    const { stateKey, elementPath } = visibleState;

    if (!stateKey || !elementPath) {
      return null;
    }

    const ownerPointer = this.innerElementPathService.getPointer(
      relativePointer,
      elementPath,
    );

    return { ownerPointer, stateKey };
  }

  activateVisibilityState(editorPointer: EditorPointer<CompRef>) {
    const resolved = this.#resolveVisibilityStateOwner(editorPointer);

    if (!resolved) {
      return;
    }

    const { ownerPointer, stateKey } = resolved;

    const manifestAPI = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: [ownerPointer],
      })
      .getAPI();

    const currentState = manifestAPI.query.getState();

    if (
      currentState === stateKey ||
      (!currentState && stateKey === 'regular')
    ) {
      return;
    }

    if (stateKey === 'regular') {
      manifestAPI.query.resetStates();
    } else {
      manifestAPI.query.setState(stateKey);
    }
  }

  #resetVisibilityState(editorPointer: EditorPointer<CompRef>) {
    const resolved = this.#resolveVisibilityStateOwner(editorPointer);

    if (!resolved) {
      return;
    }

    const manifestAPI = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: [resolved.ownerPointer],
      })
      .getAPI();

    // Only reset when the owner is actually in the visibility state — otherwise
    // we'd clear an unrelated active state on the owner. Mirrors the guard in
    // activateVisibilityState so set/reset stay symmetric.
    if (manifestAPI.query.getState() !== resolved.stateKey) {
      return;
    }

    manifestAPI.query.resetStates();
  }
}
